import React, { useState, useRef, useEffect } from "react";
import { Select, Button, Input, Tag, Collapse } from "antd";
import { LoadingOutlined, SendOutlined, RobotOutlined } from "@ant-design/icons";
import NativeVideo from "nativevideo-sdk-js";
import withMessage, { WithMessageProps } from "../hoc/withMessage";
import { initializeOrgChat, messageOrgChat } from "../services/orgChat";
import {
  OrgChatMessage,
  OrgChatRole,
  OrgChatSource,
  OrgChatConfidence,
} from "types/orgChat";
import "./orgChat.scss";

const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;

const ROLE_OPTIONS: { value: OrgChatRole; label: string }[] = [
  { value: "basic", label: "Basic" },
  { value: "manager", label: "Manager" },
  { value: "super_admin", label: "Super Admin" },
];

type OrgChatProps = {
  nvObj?: NativeVideo;
  subscriberId?: string;
  organizationName?: string;
};

// A running counter for stable client-side message ids.
let messageCounter = 0;
const nextId = () => `msg-${++messageCounter}`;

const confidenceColor = (confidence?: OrgChatConfidence) => {
  switch (confidence) {
    case "high":
      return "green";
    case "medium":
      return "orange";
    case "low":
      return "red";
    default:
      return "default";
  }
};

const formatTime = (value?: string | number | null) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return `${value}s`;
  return value;
};

const OrgChat: React.FC<OrgChatProps & WithMessageProps> = ({
  nvObj,
  organizationName,
  showMessage,
}) => {
  // ----- form (pre-chat) state -----
  const [externalId, setExternalId] = useState<string>("");
  const [role, setRole] = useState<OrgChatRole>("basic");
  const [initLoading, setInitLoading] = useState<boolean>(false);

  // ----- chat session state -----
  const [chatId, setChatId] = useState<string>();
  const [expiresAt, setExpiresAt] = useState<string>();
  const [messages, setMessages] = useState<OrgChatMessage[]>([]);
  const [input, setInput] = useState<string>("");
  const [sending, setSending] = useState<boolean>(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const handleStartChat = () => {
    if (!nvObj) {
      showMessage("Chat client is not ready yet. Please try again.", "error");
      return;
    }
    if (!externalId.trim()) {
      showMessage("Please enter an external id to start the chat.", "error");
      return;
    }

    setInitLoading(true);
    initializeOrgChat(nvObj, { context: { user_id: externalId.trim() } })
      .then(res => {
        setChatId(res.chatId);
        setExpiresAt(res.expires_at);
        setMessages([]);
        showMessage("Chat session started.", "success");
      })
      .catch(err => {
        console.log("initializeOrgChat error: ", err);
        showMessage("Could not start the chat session.", "error");
      })
      .finally(() => {
        setInitLoading(false);
      });
  };

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || !nvObj || !chatId || sending) return;

    const userMessage: OrgChatMessage = {
      id: nextId(),
      role: "user",
      content: trimmed,
    };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setSending(true);

    messageOrgChat(nvObj, {
      chatId,
      message: trimmed,
      user_context: { external_id: externalId.trim(), role },
    })
      .then(res => {
        const assistantMessage: OrgChatMessage = {
          id: nextId(),
          role: "assistant",
          content: res.response,
          sources: res.sources,
          confidence: res.confidence,
        };
        setMessages(prev => [...prev, assistantMessage]);
      })
      .catch(err => {
        console.log("messageOrgChat error: ", err);
        setMessages(prev => [
          ...prev,
          {
            id: nextId(),
            role: "assistant",
            content: "Sorry, something went wrong fetching a response.",
            isError: true,
          },
        ]);
        showMessage("Failed to send the message.", "error");
      })
      .finally(() => {
        setSending(false);
      });
  };

  const handleComposerKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    // Enter sends, Shift+Enter inserts a newline.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    setChatId(undefined);
    setExpiresAt(undefined);
    setMessages([]);
    setInput("");
  };

  const renderSources = (sources?: OrgChatSource[]) => {
    if (!sources || sources.length === 0) return null;
    return (
      <Collapse ghost className="vecna_sources">
        <Panel header={`Sources (${sources.length})`} key="sources">
          {sources.map(source => {
            const start = formatTime(source.start_time);
            const end = formatTime(source.end_time);
            return (
              <div className="vecna_source_item" key={source.id}>
                <div className="vecna_source_meta">
                  <span className="vecna_source_index">[{source.id}]</span>
                  {source.media_id && (
                    <span className="vecna_source_tag">{source.media_id}</span>
                  )}
                  {source.chunk_type && (
                    <span className="vecna_source_tag">
                      {source.chunk_type}
                    </span>
                  )}
                  {(start || end) && (
                    <span className="vecna_source_tag">
                      {start}
                      {start && end ? " – " : ""}
                      {end}
                    </span>
                  )}
                </div>
                {source.snippet && (
                  <div className="vecna_source_snippet">{source.snippet}</div>
                )}
              </div>
            );
          })}
        </Panel>
      </Collapse>
    );
  };

  // ---------------- PRE-CHAT FORM ----------------
  if (!chatId) {
    return (
      <div className="vecna_container">
        <div className="vecna_form_card">
          <div className="vecna_form_header">
            <RobotOutlined className="vecna_form_header_icon" />
            <div>
              <div className="vecna_form_title">VECNA</div>
              <div className="vecna_form_subtitle">
                Ask questions about {organizationName || "this organization"}'s
                media and conversations.
              </div>
            </div>
          </div>

          <div className="vecna_form_body">
            <div className="vecna_field">
              <label className="vecna_label">External ID</label>
              <Input
                className="vecna_input"
                placeholder="e.g. 005Ho00000Cath1AAA"
                value={externalId}
                onChange={e => setExternalId(e.target.value)}
                onPressEnter={handleStartChat}
              />
            </div>

            <div className="vecna_field">
              <label className="vecna_label">Role</label>
              <Select
                className="vecna_select"
                value={role}
                onChange={value => setRole(value as OrgChatRole)}
              >
                {ROLE_OPTIONS.map(option => (
                  <Option value={option.value} key={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </div>

            <Button
              type="primary"
              className="vecna_start_btn"
              onClick={handleStartChat}
              loading={initLoading}
              disabled={initLoading}
            >
              Start Chat
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------- CHAT INTERFACE ----------------
  return (
    <div className="vecna_container">
      <div className="vecna_chat_card">
        <div className="vecna_chat_header">
          <div className="vecna_chat_header_left">
            <RobotOutlined className="vecna_chat_header_icon" />
            <div>
              <div className="vecna_chat_title">VECNA</div>
              <div className="vecna_chat_meta">
                {externalId} · {role}
                {expiresAt ? ` · expires ${expiresAt}` : ""}
              </div>
            </div>
          </div>
          <Button size="small" onClick={handleNewChat} className="vecna_new_btn">
            New Chat
          </Button>
        </div>

        <div className="vecna_messages">
          {messages.length === 0 && (
            <div className="vecna_empty">
              Start the conversation by asking a question below.
            </div>
          )}

          {messages.map(msg => (
            <div
              key={msg.id}
              className={`vecna_message vecna_message_${msg.role}`}
            >
              <div
                className={`vecna_bubble ${
                  msg.isError ? "vecna_bubble_error" : ""
                }`}
              >
                <div className="vecna_bubble_text">{msg.content}</div>
                {msg.role === "assistant" && msg.confidence && (
                  <div className="vecna_confidence">
                    <span className="vecna_confidence_label">confidence</span>
                    <Tag color={confidenceColor(msg.confidence)}>
                      {msg.confidence}
                    </Tag>
                  </div>
                )}
                {msg.role === "assistant" && renderSources(msg.sources)}
              </div>
            </div>
          ))}

          {sending && (
            <div className="vecna_message vecna_message_assistant">
              <div className="vecna_bubble vecna_bubble_typing">
                <LoadingOutlined /> Thinking…
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="vecna_composer">
          <TextArea
            className="vecna_composer_input"
            placeholder="Ask VECNA anything about this org…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleComposerKeyDown}
            autoSize={{ minRows: 1, maxRows: 5 }}
            disabled={sending}
          />
          <Button
            type="primary"
            className="vecna_send_btn"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={sending}
            disabled={sending || !input.trim()}
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  );
};

export default withMessage(OrgChat);
